import { EditorView } from "@codemirror/view";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { TagKind } from "@redotech/redo-common-model-tags-common/tag";
import { omsAutomationCodeStepStarterCode } from "@redotech/redo-extensions-common";
import { OmsCodeStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import PenIcon from "@redotech/redo-icons/edit-03_filled.svg";
import PackageIcon from "@redotech/redo-icons/package_filled.svg";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { CodeEditorInputField } from "@redotech/redo-web/code-editor";
import {
  EntityChipClickInfo,
  EntityChipEntry,
  entityChipsExtension,
} from "@redotech/redo-web/code-editor-extensions/entity-chips";
import { ExtensionArtifactCodeEditorModal } from "@redotech/redo-web/extension-artifact-code-editor-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useRef, useState } from "react";

import { T, t, useTranslation } from "@redotech/redo-i18n";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "..";
import { ExtensionArtifactAgentCoderChat } from "../../../../../extensions/extension-artifact-agent-coder-chat";

type CodeStep = PartialWithRequiredType<OmsCodeStep>;

const DEFAULT_CONTRACT_ID = "oms-automation.code-step@2026-05.1";

function CONTRACT_OPTIONS() {
  return [
    {
      id: DEFAULT_CONTRACT_ID,
      value: DEFAULT_CONTRACT_ID,
      type: "text" as const,
      text: t("OMS code step (v1)"),
    },
  ];
}

const CodeStepEditorModal = memo(function CodeStepEditorModal({
  isOpen,
  onClose,
  step,
  setStep,
  readonly,
}: {
  isOpen: boolean;
  onClose: () => void;
  step: CodeStep;
  setStep: (step: CodeStep) => void;
  readonly?: boolean;
}) {
  const { t } = useTranslation();
  const rpc = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const merchantRpc = useRequiredContext(RedoMerchantRpcClientContext);
  const platformRpc = useRequiredContext(RedoPlatformAuthRpcClientContext);

  const [selectorDropdownOpen, setSelectorDropdownOpen] = useState(false);
  const [selectorAnchor, setSelectorAnchor] =
    useState<HTMLButtonElement | null>(null);

  const parcelsLoad = useLoad(
    async (_signal) => {
      try {
        const { parcels } = await rpc.getParcels(null);
        return parcels;
      } catch {
        return [];
      }
    },
    [rpc],
  );
  const locationsLoad = useLoad(
    async (_signal) => {
      try {
        const { locations } = await rpc.getCrdbLocations(null);
        return locations;
      } catch {
        return [];
      }
    },
    [rpc],
  );
  const shipmentTagsLoad = useLoad(
    async (signal) => {
      try {
        const { tags } = await merchantRpc.getTeamTags(
          { kind: TagKind.FULFILLMENT_GROUP },
          { signal },
        );
        return tags;
      } catch {
        return [];
      }
    },
    [merchantRpc],
  );
  const usersLoad = useLoad(
    async (_signal) => {
      try {
        const { users } = await platformRpc.getTeamUsers(null);
        return users;
      } catch {
        return [];
      }
    },
    [platformRpc],
  );
  const carrierServicesLoad = useLoad(
    async (_signal) => {
      try {
        const { options } = await rpc.getCarrierServiceCombinations({
          includeDisabled: false,
          includeMerchantCarriers: true,
        });
        return options;
      } catch {
        return [];
      }
    },
    [rpc],
  );

  // The editor toolbar has an "Insert reference" menu that drops a literal
  // at the cursor. Two formats:
  //   - chip kinds (parcel, location, …): id is a stable string; reference
  //     pick inserts the first id's "literal", chip extension renders it as
  //     a pill, click-to-edit swaps the underlying id.
  //   - literal kinds (carrierService): the artifact references the entity
  //     by an inline object literal, not by id. Reference pick opens a
  //     sub-picker; the chosen item inserts an object literal at cursor;
  //     no chip.
  type RegisteredKind =
    | {
        kind: string;
        label: string;
        format: "chip";
        entitiesById: Map<string, EntityChipEntry>;
        pickerItems: RedoListItem<string>[];
      }
    | {
        kind: string;
        label: string;
        format: "literal";
        pickerItems: RedoListItem<string>[];
        literalFor: (value: string) => string | null;
      };
  const registeredKinds = useMemo<RegisteredKind[]>(() => {
    const buildChip = (
      kind: string,
      label: string,
      entities: { id: string; name: string }[],
    ): RegisteredKind => {
      const entitiesById = new Map<string, EntityChipEntry>();
      for (const e of entities) {
        entitiesById.set(e.id.toLowerCase(), { id: e.id, name: e.name });
      }
      const pickerItems = [...entitiesById.values()]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map<
          TextRedoListItem<string>
        >((e) => ({ id: e.id, type: "text", text: e.name, supportingText: e.id, value: e.id }));
      return { kind, label, format: "chip", entitiesById, pickerItems };
    };
    const parcels = (parcelsLoad?.value ?? [])
      .map((p) => ({ id: p._id?.toString() ?? "", name: p.name }))
      .filter((p) => p.id);
    const locations = (locationsLoad?.value ?? [])
      .map((l: { id: string; name: string }) => ({ id: l.id, name: l.name }))
      .filter((l) => l.id);
    const shipmentTags = (shipmentTagsLoad?.value ?? [])
      .map((t) => ({ id: t._id?.toString() ?? "", name: t.name }))
      .filter((t) => t.id);
    const users = (usersLoad?.value ?? [])
      .map((u) => ({ id: u._id.toString(), name: u.name }))
      .filter((u) => u.id);

    // Carrier service literal kind. Output actions reference carriers by
    // `(carrier, service)` pair, not by id, so we insert an object literal.
    const carrierOptions = carrierServicesLoad?.value ?? [];
    const carrierByKey = new Map<
      string,
      { carrier: string; service: string }
    >();
    const carrierItems: TextRedoListItem<string>[] = [];
    for (const opt of carrierOptions) {
      const key = `${opt.carrier}:${opt.service}`;
      if (carrierByKey.has(key)) {
        continue;
      }
      carrierByKey.set(key, { carrier: opt.carrier, service: opt.service });
      carrierItems.push({
        id: key,
        type: "text",
        text: `${opt.carrier} • ${opt.service}`,
        supportingText: opt.carrier,
        value: key,
      });
    }
    carrierItems.sort((a, b) => a.text.localeCompare(b.text));

    return [
      buildChip("parcel", t("Package"), parcels),
      buildChip("location", t("Location"), locations),
      buildChip("shipmentTag", t("Fulfillment tag"), shipmentTags),
      buildChip("user", t("User"), users),
      {
        kind: "carrierService",
        label: t("Carrier service"),
        format: "literal",
        pickerItems: carrierItems,
        literalFor: (value) => {
          const opt = carrierByKey.get(value);
          if (!opt) {
            return null;
          }
          return `{ carrier: ${JSON.stringify(opt.carrier)}, service: ${JSON.stringify(opt.service)} }`;
        },
      },
    ];
  }, [
    parcelsLoad?.value,
    locationsLoad?.value,
    shipmentTagsLoad?.value,
    usersLoad?.value,
    carrierServicesLoad?.value,
    t,
  ]);

  const editorViewRef = useRef<EditorView | null>(null);
  const insertButtonRef = useRef<HTMLButtonElement | null>(null);
  const [referenceMenuOpen, setReferenceMenuOpen] = useState(false);
  const [chipPicker, setChipPicker] = useState<{
    kind: string;
    anchor: HTMLElement;
    range: { from: number; to: number };
    currentId: string;
  } | null>(null);

  const handleChipClick = useHandler((info: EntityChipClickInfo) => {
    setChipPicker({
      kind: info.kind,
      anchor: info.element,
      range: { from: info.from, to: info.to },
      currentId: info.id,
    });
  });

  const codeEditorExtensions = useMemo(
    () => [
      entityChipsExtension(
        registeredKinds
          .filter(
            (k): k is Extract<RegisteredKind, { format: "chip" }> =>
              k.format === "chip",
          )
          .map((k) => ({ kind: k.kind, entitiesById: k.entitiesById })),
        { onChipClick: handleChipClick },
      ),
    ],
    [registeredKinds, handleChipClick],
  );

  const insertAtCursor = useHandler((text: string) => {
    const view = editorViewRef.current;
    if (!view) {
      return;
    }
    const cursor = view.state.selection.main.head;
    view.dispatch({
      changes: { from: cursor, to: cursor, insert: text },
      selection: { anchor: cursor + text.length },
    });
    view.focus();
  });

  const handleChipPicked = useHandler((item: RedoListItem<string>) => {
    const view = editorViewRef.current;
    if (!view || !chipPicker) {
      setChipPicker(null);
      return;
    }
    view.dispatch({
      changes: {
        from: chipPicker.range.from,
        to: chipPicker.range.to,
        insert: `"${item.value}"`,
      },
    });
    setChipPicker(null);
  });

  const chipPickerItems = useMemo<RedoListItem<string>[]>(
    () =>
      chipPicker
        ? (registeredKinds.find((k) => k.kind === chipPicker.kind)
            ?.pickerItems ?? [])
        : [],
    [chipPicker, registeredKinds],
  );

  const referenceMenuItems = useMemo<RedoListItem<string>[]>(
    () =>
      registeredKinds
        .filter((k) => k.pickerItems.length > 0)
        .map((k) => ({
          id: k.kind,
          type: "text",
          text: k.label,
          value: k.kind,
        })),
    [registeredKinds],
  );

  // Sub-picker shown when the reference menu picks a literal-format kind
  // (carrier services). Anchored to the toolbar button.
  const [literalPicker, setLiteralPicker] = useState<{
    kind: string;
    anchor: HTMLElement;
  } | null>(null);

  const handleReferenceMenuPicked = useHandler((item: RedoListItem<string>) => {
    setReferenceMenuOpen(false);
    const k = registeredKinds.find((rk) => rk.kind === item.value);
    if (!k) {
      return;
    }
    if (k.format === "chip") {
      // Default = first entity in sorted order. The chip then lets the
      // merchant swap to whichever entity they actually wanted.
      const first = k.pickerItems[0];
      if (first) {
        insertAtCursor(`"${first.value}"`);
      }
      return;
    }
    // Literal format: open a sub-picker so the author picks the exact
    // entry to insert — there's no chip to swap afterwards.
    if (!insertButtonRef.current) {
      return;
    }
    setLiteralPicker({ kind: k.kind, anchor: insertButtonRef.current });
  });

  const literalPickerKind = useMemo(
    () =>
      literalPicker
        ? registeredKinds.find((k) => k.kind === literalPicker.kind)
        : null,
    [literalPicker, registeredKinds],
  );

  const handleLiteralPicked = useHandler((item: RedoListItem<string>) => {
    if (literalPickerKind?.format !== "literal") {
      setLiteralPicker(null);
      return;
    }
    const literal = literalPickerKind.literalFor(item.value);
    setLiteralPicker(null);
    if (literal) {
      insertAtCursor(literal);
    }
  });

  const handleInsertReferenceClick = useHandler(() => {
    setReferenceMenuOpen((open) => !open);
  });

  const editorReloadKey =
    parcelsLoad?.pending ||
    locationsLoad?.pending ||
    shipmentTagsLoad?.pending ||
    usersLoad?.pending ||
    carrierServicesLoad?.pending
      ? "loading"
      : "ready";

  return (
    <ExtensionArtifactCodeEditorModal
      artifactId={step.artifactId}
      client={merchantRpc}
      codeEditorExtensions={codeEditorExtensions}
      codeHeaderActions={
        <RedoButton
          disabled={readonly || referenceMenuItems.length === 0}
          hierarchy="tertiary"
          IconLeading={PackageIcon}
          onClick={handleInsertReferenceClick}
          ref={insertButtonRef}
          size="xs"
          text={t("Insert reference")}
        />
      }
      contractId={DEFAULT_CONTRACT_ID}
      editorReloadKey={editorReloadKey}
      isOpen={isOpen}
      leftPaneExtras={
        <>
          <RedoListDropdown<string>
            dropdownAnchor={insertButtonRef.current}
            dropdownOpen={referenceMenuOpen}
            fitToAnchor={false}
            items={referenceMenuItems}
            itemSelected={({ item }) => handleReferenceMenuPicked(item)}
            setDropdownOpen={setReferenceMenuOpen}
            size="xs"
          />
          <RedoListDropdown<string>
            dropdownAnchor={chipPicker?.anchor ?? null}
            dropdownOpen={Boolean(chipPicker)}
            fitToAnchor={false}
            items={chipPickerItems}
            itemSelected={({ item }) => handleChipPicked(item)}
            selectedItems={
              chipPicker
                ? chipPickerItems
                    .filter((p) => p.value === chipPicker.currentId)
                    .map((p) => p.value)
                : []
            }
            setDropdownOpen={(open) => {
              if (!open) {
                setChipPicker(null);
              }
            }}
            size="xs"
          />
          <RedoListDropdown<string>
            dropdownAnchor={literalPicker?.anchor ?? null}
            dropdownOpen={Boolean(literalPicker)}
            fitToAnchor={false}
            items={literalPickerKind?.pickerItems ?? []}
            itemSelected={({ item }) => handleLiteralPicked(item)}
            setDropdownOpen={(open) => {
              if (!open) {
                setLiteralPicker(null);
              }
            }}
            size="xs"
          />
        </>
      }
      onClose={onClose}
      onEditorView={(view) => {
        editorViewRef.current = view;
      }}
      onSaved={(artifactId) => {
        setStep({ ...step, artifactId });
        onClose();
      }}
      readonly={readonly}
      renderAgentCoderPane={(pane) => (
        <ExtensionArtifactAgentCoderChat
          code={pane.code}
          contractId={pane.contractId}
          inputSelector={pane.inputSelector}
          setCode={pane.setCode}
        />
      )}
      renderRightPane={({
        dts,
        universeDts,
        topLevelKinds,
        contractId,
        setContractId,
        inputSelector,
        setInputSelector,
      }) => {
        const kindItems: RedoListItem<string>[] = topLevelKinds.map((kind) => ({
          id: kind,
          type: "text",
          text: kind,
          value: kind,
        }));
        const selectedKindItems = kindItems.filter((item) =>
          inputSelector.includes(item.value),
        );
        return (
          <>
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium">
                {t("Contract")}
              </Text>
              <RedoSingleSelectDropdownInput<string>
                fitToAnchor={false}
                flexGrow={false}
                options={CONTRACT_OPTIONS()}
                optionSelected={({ item }) => setContractId(item.value)}
                selectedItem={CONTRACT_OPTIONS().find(
                  (c) => c.value === contractId,
                )}
              />
            </Flex>

            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium">
                {t("Input selector")}
              </Text>
              <Text fontSize="xs" textColor="secondary">
                <T>
                  Pick the top-level fields your code reads. Unselected fields
                  are pruned from <code>Input</code>.
                </T>
              </Text>
              <RedoTagsDropdownInputButton<string>
                disabled={readonly}
                isDropdownOpen={selectorDropdownOpen}
                onClick={() => setSelectorDropdownOpen((open) => !open)}
                placeholder={t("Select fields...")}
                ref={setSelectorAnchor}
                removeItem={(item) => {
                  setInputSelector((prev) =>
                    prev.filter((k) => k !== item.value),
                  );
                }}
                selectedItems={selectedKindItems.map((item) => ({
                  id: item.id,
                  type: "badge",
                  badge: { text: item.value },
                  value: item.value,
                }))}
                size="xs"
              />
              <RedoMultiselectDropdown<string>
                dropdownAnchor={selectorAnchor}
                dropdownOpen={selectorDropdownOpen}
                fitToAnchor
                items={kindItems}
                selectedItems={selectedKindItems}
                selectionVariant="checkbox"
                setDropdownOpen={setSelectorDropdownOpen}
                setSelectedItems={(items) =>
                  setInputSelector(items.map((item) => item.value))
                }
                size="xs"
              />
            </Flex>

            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium">
                {t("Available input")}
              </Text>
              <Text fontSize="xs" textColor="secondary">
                {t(
                  "The Input type your code receives, narrowed by your input selector.",
                )}
              </Text>
              <CodeEditorInputField
                language="javascript"
                readonly
                setValue={() => {}}
                value={dts}
              />
              <RedoCollapseSubsection title={t("Show full schema")}>
                <Flex dir="column" gap="xs" pt="xs">
                  <Text fontSize="xs" textColor="secondary">
                    {t(
                      "Everything the contract can expose. Toggle fields in the input selector above to bring them into Input.",
                    )}
                  </Text>
                  <CodeEditorInputField
                    language="javascript"
                    readonly
                    setValue={() => {}}
                    value={universeDts}
                  />
                </Flex>
              </RedoCollapseSubsection>
            </Flex>
          </>
        );
      }}
      starterCode={omsAutomationCodeStepStarterCode}
      subtitle={t(
        "Run merchant-authored {{languageName}} against the OMS automation context.",
        { languageName: "TypeScript" },
      )}
      title={t("Edit code step")}
    />
  );
});

const OmsCodeInputs = memo(function OmsCodeInputs(
  props: StepTypeDetailsProps<CodeStep>,
) {
  const { step, setStep, readonly } = props;
  const { t } = useTranslation();
  const [editorOpen, setEditorOpen] = useState(false);

  return (
    <Flex dir="column" gap="lg" p="xl" w="full">
      <Flex dir="column" gap="xs">
        <Text fontSize="lg" fontWeight="semibold">
          {t("Code step")}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          {t(
            "Run merchant-authored {{languageName}} against the OMS automation context.",
            { languageName: "TypeScript" },
          )}
        </Text>
      </Flex>

      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          {t("Name")}
        </Text>
        <RedoTextInput
          placeholder={t("Untitled code step")}
          setValue={(name) =>
            setStep({ ...step, name: name.length > 0 ? name : undefined })
          }
          size="sm"
          state={readonly ? "disabled" : "default"}
          value={step.name ?? ""}
        />
      </Flex>

      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          {t("Artifact")}
        </Text>
        {step.artifactId ? (
          <Text fontFamily="monospace" fontSize="xs" textColor="secondary">
            {step.artifactId}
          </Text>
        ) : (
          <Text fontSize="xs" textColor="secondary">
            {t("No artifact uploaded")}
          </Text>
        )}
      </Flex>

      <Flex>
        <RedoButton
          disabled={readonly}
          hierarchy="primary"
          IconLeading={PenIcon}
          onClick={() => setEditorOpen(true)}
          text={step.artifactId ? t("Edit code") : t("Add code")}
        />
      </Flex>

      <CodeStepEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        readonly={readonly}
        setStep={setStep}
        step={step}
      />
    </Flex>
  );
});

export const omsCodeStepType: FrontendStepType<CodeStep> = {
  type: "custom",
  valid: (step) => Boolean(step.artifactId && step.nextId),
  customInspector: OmsCodeInputs,
};
